import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import SiteHeader from '../components/SiteHeader';
import SiteBackground from '../components/SiteBackground';
import api from '../utils/api';

const NewsListPage = () => {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchNews = async () => {
      try {
        const response = await api.get('/news/published');
        setNewsItems(response.data || []);
      } catch (err) {
        console.error('ニュースの取得に失敗しました:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="min-h-screen text-gray-900 relative">
      <SiteBackground />
      <SiteHeader />

      <div className="fixed top-3 right-4 sm:top-5 sm:right-6 z-[60]">
        <Button
          variant="outline"
          size="sm"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
          className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
          data-testid="news-list-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          戻る
        </Button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-56 sm:pt-40 pb-20 relative mt-[5mm]">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent" data-testid="news-list-title">
            Latest News
          </h1>
        </div>

        {loading ? (
          <div className="text-center text-gray-600 py-20" data-testid="news-list-loading">読み込み中...</div>
        ) : newsItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 mb-6" data-testid="news-list-empty">現在、お知らせはありません。</p>
            <Link to="/" className="inline-flex items-center text-amber-500 hover:text-amber-600">
              <ChevronLeft className="w-4 h-4 mr-1" />
              トップに戻る
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="news-list-grid">
            {newsItems.map((news) => (
              <Link
                to={`/news/${news.id}`}
                key={news.id}
                data-testid={`news-list-card-${news.id}`}
                className="group relative rounded-2xl bg-gradient-to-br from-gray-50/80 to-gray-100/80 border border-amber-500/20 backdrop-blur-sm overflow-hidden hover:border-amber-500/50 transition-all duration-500 transform hover:-translate-y-2 block"
              >
                {news.image_url && (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={news.image_url}
                      alt={news.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-50 to-transparent"></div>
                  </div>
                )}
                <div className="relative p-6">
                  <div className="flex items-center space-x-3 mb-3 flex-wrap gap-y-2">
                    <span className="px-3 py-1 bg-amber-500/20 text-amber-500 text-xs rounded-full border border-amber-500/30">
                      {news.category}
                    </span>
                    <span className="text-gray-500 text-sm">{news.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-amber-500 transition-colors mb-3 line-clamp-2">
                    {news.title}
                  </h3>
                  {news.excerpt && (
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">{news.excerpt}</p>
                  )}
                  <span className="inline-flex items-center text-amber-500 text-sm group-hover:translate-x-1 transition-transform">
                    詳しく見る
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default NewsListPage;
